import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './ImageCarousel.css';

const ImageCarousel = ({ images, autoPlayInterval = 4000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (autoPlayInterval > 0) {
      const interval = setInterval(() => {
        setDirection(1);
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, autoPlayInterval);

      return () => clearInterval(interval);
    }
  }, [images.length, autoPlayInterval]);

  const paginate = (newDirection) => {
    setDirection(newDirection);
    if (newDirection === 1) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    } else {
      setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
    }
  };

  const goToSlide = (index) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  // Función para obtener el índice relativo de cada imagen
  const getRelativeIndex = (index) => {
    const diff = index - currentIndex;
    if (diff > images.length / 2) return diff - images.length;
    if (diff < -images.length / 2) return diff + images.length;
    return diff;
  };

  // Renderizar las imágenes visibles (centro y los 2 de cada lado)
  const getVisibleSlides = () => {
    const visible = [];
    // En móvil, solo mostrar la imagen central
    const range = isMobile ? 0 : 2;
    
    for (let i = -range; i <= range; i++) {
      const index = (currentIndex + i + images.length) % images.length;
      visible.push({ index, position: i });
    }
    return visible;
  };

  return (
    <div className="carousel-3d-container">
      <div className="carousel-3d-wrapper">
        <div className="carousel-3d-stage">
          {getVisibleSlides().map(({ index, position }) => {
            const isCenter = position === 0;
            const zIndex = isCenter ? 50 : 5 - Math.abs(position);
            
            return (
              <motion.div
                key={index}
                className={`carousel-3d-slide ${isCenter ? 'active' : ''}`}
                style={{
                  zIndex,
                }}
                initial={false}
                animate={{
                  x: isMobile ? 0 : `${position * 280}px`,
                  y: isMobile ? 0 : (isCenter ? -20 : 0),
                  scale: isMobile ? (isCenter ? 1 : 0) : (isCenter ? 1.15 : 0.7),
                  rotateY: isMobile ? 0 : position * 30,
                  opacity: isMobile ? (isCenter ? 1 : 0) : (Math.abs(position) <= 2 ? 1 - Math.abs(position) * 0.3 : 0),
                }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 30,
                }}
                onClick={() => !isCenter && goToSlide(index)}
              >
                <div className="carousel-3d-image-wrapper">
                  <img
                    src={images[index]}
                    alt={`Barbershop ${index + 1}`}
                    className="carousel-3d-image"
                  />
                  {!isCenter && <div className="carousel-3d-overlay" />}
                </div>
              </motion.div>
            );
          })}
        </div>

        <button
          className="carousel-3d-button carousel-3d-button-prev"
          onClick={() => paginate(-1)}
          aria-label="Imagen anterior"
        >
          <ChevronLeft size={24} />
        </button>
        <button
          className="carousel-3d-button carousel-3d-button-next"
          onClick={() => paginate(1)}
          aria-label="Imagen siguiente"
        >
          <ChevronRight size={24} />
        </button>
      </div>

      {/* Indicadores de puntos */}
      <div className="carousel-3d-indicators">
        {images.map((_, index) => (
          <button
            key={index}
            className={`carousel-3d-indicator ${index === currentIndex ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Ir a la imagen ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageCarousel;
